import { RPCResponses } from "@/modules/communication";
import Xmpp from "@/modules/Xmpp";
import { isPairedDevice } from "@/utils";
import { cameraStore } from "camera/store/camera";
import Peers from "@/modules/Peers";
import EventManager from "camera/modules/events/EventManager";
import { stopFaceIfrunning } from "./utils";
import { stationStore } from "camera/store/station";
import ManualVideoManager from "./ManualVideoManager";

type Init = {
  stationManager: CameraStationManager;
  rtcManager: CameraRtcManager;
  manualVideoManager: ManualVideoManager;
  rpcEventUpdater: RpcEventUpdater;
  changeCamera: (cameraId: string, jidToIgnore?: string) => Promise<void>;
  changeCameraStationRecordingMode: (mode: CameraStationRecordingMode, jidToIgnore?: string) => void;
  setCameraAlarm: (state: AlarmState, jidToIgnore?: string) => Promise<void>;
  setCameraVideoRecording: (state: CameraRecordingState, jidToIgnore?: string) => Promise<CameraRecordingStatus>;
};
type StartMonitoringPayload = Parameters<typeof RPCResponses.StartMonitoring.create>[0];

const { setActiveFaceJid } = cameraStore();

export default class Handlers {
  private xmpp = Xmpp.getInstance();
  private stationManager!: CameraStationManager;
  private rtcManager!: CameraRtcManager;
  private manualVideoManager!: Init["manualVideoManager"];
  private _changeCamera!: Init["changeCamera"];
  private _changeCameraStationRecordingMode!: Init["changeCameraStationRecordingMode"];
  private _setCameraAlarm!: Init["setCameraAlarm"];
  private _setCameraVideoRecording!: Init["setCameraVideoRecording"];
  private rpcEventUpdater!: RpcEventUpdater;
  private get cameraList() {
    return cameraStore().cameraList;
  }
  private get cameraId() {
    return cameraStore().cameraId;
  }

  init = ({
    stationManager,
    rtcManager,
    rpcEventUpdater,
    changeCamera,
    changeCameraStationRecordingMode,
    setCameraAlarm,
    manualVideoManager,
    setCameraVideoRecording
  }: Init) => {
    this.stationManager = stationManager;
    this.rtcManager = rtcManager;
    this.rpcEventUpdater = rpcEventUpdater;
    this._changeCamera = changeCamera;
    this._changeCameraStationRecordingMode = changeCameraStationRecordingMode;
    this._setCameraAlarm = setCameraAlarm;
    this._setCameraVideoRecording = setCameraVideoRecording;
    this.manualVideoManager = manualVideoManager;
  };

  startMonitoring = async (
    request: { webRTCCreateOffer?: { sdp: string } },
    from: string,
    sendResponse: RpcSendResponse
  ) => {
    log.monitoring("received start monitoring request", request, from);
    const isCameraOff = stationStore().recordingMode === "OFF";
    if (!isPairedDevice(from) || isCameraOff) return;

    const availableCameras = this.cameraList
      ? this.cameraList.map((camera) => ({
          cameraId: camera.deviceId,
          name: camera.label
        }))
      : [];

    const responsePayload: StartMonitoringPayload = {
      batteryStatus: this.stationManager.getBatteryStatus(),
      cameraState: { cameraId: this.cameraId! },
      configuration: { availableCameras },
      alarmStatus: this.stationManager.getAlarmStatus(),
      videoRecordingStatus: this.manualVideoManager.getStatus()
    };

    const offerSdp = request.webRTCCreateOffer?.sdp;
    if (offerSdp) {
      try {
        log.monitoring("instant video: offer received, creating answer");
        const answerSdp = await this.rtcManager.initializeConnectionWithOffer(from, offerSdp);
        responsePayload.webRTCCreateAnswer = { sdp: answerSdp };
      } catch (err) {
        log.err("instant video failed, falling back to legacy flow", err);
        this.rtcManager.rtc.destroyPeerConnection(from);
        this.rtcManager.initializeConnection(from);
      }
    } else {
      this.rtcManager.initializeConnection(from);
    }

    const response = RPCResponses.StartMonitoring.create(responsePayload);
    sendResponse(response);
    this.xmpp.sendPresence("sender_connected");
    EventManager.triggerEvent("CONNECT", from);
  };

  stopMonitoring = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    this.rtcManager.rtc.destroyPeerConnection(from);
    const response = RPCResponses.StopMonitoring.create();
    sendResponse(response);
    stopFaceIfrunning(from);
    const isCameraOn = stationStore().recordingMode === "ON";
    if (Object.keys(Peers.getAll()).length === 0 && isCameraOn) {
      this.xmpp.sendPresence("sender");
    }
    EventManager.triggerEvent("DISCONNECT", from);
  };

  startVideoBroadcast = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    const response = RPCResponses.StartVideoBroadcasting.create({
      result: "SUCCESS",
      cameraId: this.cameraId!
    });
    sendResponse(response);
  };

  ping = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    sendResponse(RPCResponses.Ping.create());
  };

  changeCamera = async (request: ChangeCameraRequest, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    await this._changeCamera(request.cameraId, from);
    const response = RPCResponses.CameraCommand.create({
      cameraId: this.cameraId!,
      result: "SUCCESS"
    });
    sendResponse(response);
  };

  startVideoReceiving = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    const previewAspectRatio = window.innerWidth / window.innerHeight;
    const hasFaceActive = Boolean(cameraStore().activeFaceJid);
    const response = RPCResponses.StartVideoReceiving.create({
      result: hasFaceActive ? "ALREADY_RUNNING" : "SUCCESS",
      previewAspectRatio
    });
    if (!hasFaceActive) setActiveFaceJid(from);
    sendResponse(response);
  };

  stopVideoReceiving = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    setActiveFaceJid(null);
    sendResponse(null);
  };

  startCameraHistoryReplayEvents = async (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'startCameraHistoryReplayEvents' request");
    this.rpcEventUpdater.enableForPeer(from);
    const payload = await this.rpcEventUpdater.getPayloadForPeer(from);

    const response = RPCResponses.CameraHistoryReplayEvents.create(payload);
    sendResponse(response);
  };

  reloadCameraHistoryReplayEvents = async (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'reloadCameraHistoryReplayEvents' request");
    const payload = await this.rpcEventUpdater.getPayloadForPeer(from);

    const response = RPCResponses.CameraHistoryReplayEvents.create(payload);
    sendResponse(response);
  };

  stopCameraHistoryReplayEvents = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'stopCameraHistoryReplayEvents' request");

    const response = RPCResponses.StopCameraHistoryReplayEvents.create();
    this.rpcEventUpdater.disableForPeer(from);
    sendResponse(response);
  };

  syncMonitoring = (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'syncMonitoring' request");

    const response = RPCResponses.SyncMonitoring.create({
      batteryStatus: this.stationManager.getBatteryStatus(),
      cameraState: { cameraId: this.cameraId! },
      alarmStatus: this.stationManager.getAlarmStatus(),
      videoRecordingStatus: this.manualVideoManager.getStatus()
    });
    sendResponse(response);
  };

  changeCameraStationRecordingMode = (
    request: ChangeCameraStationRecordingModeRequest,
    from: string,
    sendResponse: RpcSendResponse
  ) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'changeCameraStationRecordingMode' request ", request);
    this._changeCameraStationRecordingMode(request.mode, from);

    const response = RPCResponses.ChangeCameraStationRecordingMode.create();
    sendResponse(response);
  };

  setCameraAlarm = async (request: SetCameraAlarmRequest, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'setCameraAlarm' request ", request);

    await this._setCameraAlarm(request.status.state, from);
    const response = RPCResponses.SetCameraAlarm.create({ result: "SUCCESS", status: request.status });
    sendResponse(response);
  };

  getDetectionZonesPreview = async (request: {}, from: string, sendResponse: RpcSendResponse) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'getDetectionZonesPreview' request");
    const preview = await this.stationManager.getDetectionZonesPreview();
    const response = RPCResponses.GetDetectionZonesPreview.create("SUCCESS", {
      ...preview,
      cameraId: this.cameraId!
    });
    sendResponse(response);
  };

  setCameraVideoRecording = async (
    request: SetCameraVideoRecordingRequest,
    from: string,
    sendResponse: RpcSendResponse
  ) => {
    if (!isPairedDevice(from)) return;
    log.rpc("Received 'setCameraVideoRecording' request ", request);

    const status = await this._setCameraVideoRecording(request.state, from);
    const response = RPCResponses.SetCameraVideoRecording.create({ result: "SUCCESS", status });
    sendResponse(response);
  };
}
